import { buildReplayTrack, sampleReplay, type TrackPoint } from './FlightReplay3D.motion.ts';

export interface ReplayFlight {
  id: string;
  pilotId: number;
  label: string;
  color: string;
  track: TrackPoint[];
}

export function buildReplayFleet(flights: ReplayFlight[]) {
  const valid = flights.filter(flight => flight.track.length);
  const starts = valid.map(flight => Date.parse(flight.track[0].timestamp) / 1000);
  const finiteStarts = starts.filter(Number.isFinite);
  const start = finiteStarts.length ? Math.min(...finiteStarts) : 0;
  const tracks = valid.map((flight, index) => {
    const points = buildReplayTrack(flight.track);
    const offset = Number.isFinite(starts[index]) ? starts[index] - start : 0;
    return { ...flight, points, offset, duration: points[points.length - 1].elapsedSeconds };
  });
  tracks.sort((a, b) => a.offset - b.offset);
  return { tracks, duration: tracks.reduce((end, track) => Math.max(end, track.offset + track.duration), 0) };
}

export function sampleFleet(fleet: ReturnType<typeof buildReplayFleet>, progress: number) {
  const seconds = Math.max(0, Math.min(1, progress)) * fleet.duration;
  return fleet.tracks.map(track => {
    const localProgress = track.duration ? Math.max(0, Math.min(1, (seconds - track.offset) / track.duration)) : 0;
    return { ...track, progress: localProgress, started: seconds >= track.offset, sample: sampleReplay(track.points, localProgress)! };
  });
}

// For multiple flights by a pilot, follow the latest flight that has started.
export function followedFlight(samples: ReturnType<typeof sampleFleet>, pilotId: number) {
  const flights = samples.filter(flight => flight.pilotId === pilotId);
  return flights.filter(flight => flight.started).sort((a, b) => b.offset - a.offset)[0]
    ?? flights.sort((a, b) => a.offset - b.offset)[0];
}
